import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';

import { FitDataService, OutlierSuspectSession } from '../../training-analysis/fit-data.service';
import { PowerOutlierDialogComponent } from './power-outlier-dialog.component';

/**
 * Auflistung aller Sessions mit erkannten Power-Ausreißern. Wird vom
 * (kompakten) Banner geöffnet. Klick auf eine Zeile öffnet den
 * Inspektions-/Korrektur-Dialog.
 */
@Component({
  selector: 'app-outlier-list-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ 'calendar.outlier.dialog_list_title' | translate }}</h2>
    <mat-dialog-content class="ol-content">
      @if (loading) {
        <div class="ol-loading"><mat-spinner diameter="28"></mat-spinner></div>
      } @else if (sessions.length === 0) {
        <p class="ol-empty">{{ 'calendar.outlier.none' | translate }}</p>
      } @else {
        <p class="ol-intro">
          {{ 'calendar.outlier.banner_body' | translate:{ n: sessions.length } }}
        </p>
        @for (s of sessions; track s.session_id) {
          <button type="button" class="ol-row" (click)="inspect(s)">
            <div class="ol-row__main">
              <strong>{{ s.name || (s.sport || 'Training') }}</strong>
              <span class="ol-row__date">{{ s.date }}</span>
            </div>
            <span class="ol-row__count">
              {{ s.candidate_count }} {{ 'calendar.outlier.ranges' | translate }}
            </span>
            <mat-icon>chevron_right</mat-icon>
          </button>
        }
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button [mat-dialog-close]="{ changed: changed }">
        {{ 'calendar.close' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .ol-content { min-width: 26rem; max-width: 90vw; }
    .ol-loading { display: flex; justify-content: center; padding: 1rem 0; }
    .ol-empty, .ol-intro { color: var(--text-secondary, #888); font-size: var(--fs-sm); }
    .ol-intro { margin: 0 0 0.6rem; }
    .ol-row {
      display: flex; align-items: center; gap: 0.6rem; width: 100%;
      padding: 0.5rem 0.6rem; margin-bottom: 0.3rem;
      border: 1px solid var(--border-color, #e0e0e0); border-radius: 6px;
      background: var(--surface, #fff); cursor: pointer; text-align: left;
      font: inherit; color: inherit;
    }
    .ol-row:hover { background: var(--surface-hover, #f5f5f5); }
    .ol-row__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
    .ol-row__date { font-size: var(--fs-xs); color: var(--text-secondary, #888); }
    .ol-row__count {
      font-size: var(--fs-xs); color: #e65100; white-space: nowrap;
    }
  `],
})
export class OutlierListDialogComponent implements OnInit {
  private readonly fitData = inject(FitDataService);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  loading = true;
  changed = false;
  sessions: OutlierSuspectSession[] = [];

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.fitData.getOutlierStatus().subscribe({
      next: (s) => {
        this.sessions = s?.sessions ?? [];
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => { this.sessions = []; this.loading = false; this.cdr.markForCheck(); },
    });
  }

  inspect(s: OutlierSuspectSession): void {
    this.dialog.open(PowerOutlierDialogComponent, {
      width: '44rem',
      maxWidth: '95vw',
      data: {
        sessionId: s.session_id,
        title: s.name || (s.sport ?? 'Training'),
        date: s.date,
      },
    }).afterClosed().subscribe((result) => {
      if (result?.changed) {
        this.changed = true;
        this.load();
      }
    });
  }
}
